import { useMemo, useState } from 'react'
import { Calendar } from 'react-big-calendar'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import { localizer, toDateTime, dateToDateStr, dateToTimeStr } from '../../../../shared/calendar/localizer'
import Button from '../../../../shared/components/Button/Button'
import useFlashMessage from '../../../../hooks/useFlashMessage'
import './TechnicianAgendaModal.css'

const DAY_MIN = new Date(1970, 0, 1, 0, 0)
const DAY_MAX = new Date(1970, 0, 1, 23, 59, 59)

const MESSAGES = {
    week: 'Semana',
    day: 'Dia',
    today: 'Hoje',
    previous: 'Anterior',
    next: 'Próximo',
    noEventsInRange: 'Nenhum horário ocupado neste período',
}

function TechnicianAgendaModal({ technicians, serviceOrders, excludeOrderId, initialDate, onConfirm, onClose }) {
    const [currentDate, setCurrentDate] = useState(initialDate ? new Date(`${initialDate}T00:00:00`) : new Date())
    const [scrollToTime] = useState(() => new Date())
    const [pendingSlot, setPendingSlot] = useState(null)
    const { setFlashMessage } = useFlashMessage()

    const technicianIds = useMemo(
        () => technicians.map((t) => t._id || t.value),
        [technicians],
    )

    const busyEvents = useMemo(() => {
        return (serviceOrders || [])
            .filter((order) => order._id !== excludeOrderId)
            .filter((order) => order.scheduledDate && order.scheduledStartTime && order.scheduledEndTime)
            .filter((order) => (order.technicians || []).some((t) => technicianIds.includes(t._id || t.value)))
            .map((order) => {
                const names = (order.technicians || []).map((t) => t.name || t.label).join(', ')
                return {
                    title: `Ocupado — ${order.code}${names ? ` (${names})` : ''}`,
                    start: toDateTime(order.scheduledDate, order.scheduledStartTime),
                    end: toDateTime(order.scheduledDate, order.scheduledEndTime),
                    isBusy: true,
                }
            })
    }, [serviceOrders, technicianIds, excludeOrderId])

    function overlapsBusy(start, end) {
        return busyEvents.some((ev) => start < ev.end && end > ev.start)
    }

    function handleSelectSlot({ start, end }) {
        if (start < new Date()) {
            setFlashMessage('Não é possível selecionar um horário que já passou.', 'error')
            setPendingSlot(null)
            return
        }
        if (overlapsBusy(start, end)) {
            setFlashMessage('Esse horário já está ocupado por um dos técnicos selecionados.', 'error')
            setPendingSlot(null)
            return
        }
        setPendingSlot({ start, end })
    }

    function handleConfirm() {
        if (!pendingSlot) return
        onConfirm({
            scheduledDate: dateToDateStr(pendingSlot.start),
            scheduledStartTime: dateToTimeStr(pendingSlot.start),
            scheduledEndTime: dateToTimeStr(pendingSlot.end),
        })
    }

    const events = pendingSlot
        ? [...busyEvents, { title: 'Selecionado', start: pendingSlot.start, end: pendingSlot.end, isPending: true }]
        : busyEvents

    function eventPropGetter(event) {
        return { className: event.isPending ? 'tam-event-pending' : 'tam-event-busy' }
    }

    function slotPropGetter(date) {
        return date < new Date() ? { className: 'tam-slot-past' } : {}
    }

    return (
        <div className="tam-overlay" onClick={onClose}>
            <div className="tam-box" onClick={(e) => e.stopPropagation()}>
                <div className="box-x"><div onClick={onClose} className="shd-modal-close">X</div></div>

                <div className="tam-header">
                    <div className="tam-title-block">
                        <p className="tam-title">Escolha um horário</p>
                        <span className="tam-title-accent" />
                    </div>
                    <div className="tam-actions">
                        <Button variant="save" onClick={handleConfirm} disabled={!pendingSlot}>Salvar</Button>
                        <Button variant="cancel" onClick={onClose}>Cancelar</Button>
                    </div>
                    <div className="tam-technicians">
                        {technicians.map((t) => (
                            <span className="tam-technician-badge" key={t._id || t.value}>{t.name || t.label}</span>
                        ))}
                    </div>
                </div>

                <div className="tam-calendar">
                    <Calendar
                        localizer={localizer}
                        events={events}
                        defaultView="week"
                        views={['week', 'day']}
                        date={currentDate}
                        onNavigate={setCurrentDate}
                        step={15}
                        timeslots={1}
                        min={DAY_MIN}
                        max={DAY_MAX}
                        scrollToTime={scrollToTime}
                        selectable
                        onSelectSlot={handleSelectSlot}
                        eventPropGetter={eventPropGetter}
                        slotPropGetter={slotPropGetter}
                        culture="pt-BR"
                        messages={MESSAGES}
                    />
                </div>

                <div className="tam-footer">
                    <div className="tam-selected-info">
                        {pendingSlot
                            ? `Selecionado: ${dateToDateStr(pendingSlot.start)} das ${dateToTimeStr(pendingSlot.start)} às ${dateToTimeStr(pendingSlot.end)}`
                            : 'Clique e arraste na agenda para escolher o horário'}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default TechnicianAgendaModal
